import { FC, useState } from 'react';

import { useFormatter } from '@eneco/i18n';
import { Bleed, Box, Divider, Expandable, Text } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { Fields } from '@sitecore/types/manual/smeFlowExperiment/SMEOfferStep';
import { FlowsNewEnergyOfferStep } from '@sitecore/types/SMEOfferStep';

import { Ribbon, SMEFlowProductCard, TariffDetailsOverview } from '../../components';
import { FlowContextSme } from '../../context';
import {
  checkOfferIncludesDynamicPricingProduct,
  constructTextualDataKey,
  getCashbackTexts,
  getPromotionText,
  getTitle,
  getUsps,
} from '../../helpers';
import { EnergyType, Offer } from '../../types';

interface OfferCardProps {
  offer: Offer;
  isSME?: boolean;
  energyType?: EnergyType;
  textualData?: Fields;
  flowContext?: FlowContextSme;
}

type OfferCardFooterProps = Pick<OfferCardProps, 'textualData' | 'offer' | 'flowContext'>;

const OfferCardFooter = ({ textualData, flowContext, offer }: OfferCardFooterProps) => {
  const [isPriceDetailsOpen, setIsPriceDetailsOpen] = useState(false);

  const isSmallBreakpoint = !useMediaQuery('lg');

  if (isSmallBreakpoint) {
    return (
      <Expandable isOpen={isPriceDetailsOpen} setOpen={setIsPriceDetailsOpen}>
        <Box paddingX="6" paddingY="4">
          <Expandable.Trigger isTransparent aria-labelledby={`tariff-details`}>
            {textualData?.productData?.tariffsTriggerText?.value ?? ''}
          </Expandable.Trigger>
        </Box>

        <Expandable.Content>
          <Bleed horizontal="6">
            <Divider />
          </Bleed>
          <Box paddingX={6} paddingY={3}>
            <TariffDetailsOverview offer={offer} textualData={textualData} flowContext={flowContext} />
          </Box>
        </Expandable.Content>
      </Expandable>
    );
  }

  return (
    <>
      <Box paddingX="6" paddingTop="6" paddingBottom="2">
        <Text size="BodyXS">{textualData?.productData?.tariffsTriggerText?.value ?? ''}</Text>
      </Box>
      <Bleed horizontal="6"></Bleed>
      <Box paddingX={6} paddingY={3}>
        <TariffDetailsOverview offer={offer} textualData={textualData} flowContext={flowContext} />
      </Box>
    </>
  );
};

export const SMEOfferCard: FC<OfferCardProps> = ({
  offer,
  isSME,
  energyType = EnergyType.Electricity,
  textualData,
  flowContext,
}) => {
  const { format, currency } = useFormatter();

  const textualDataKey = constructTextualDataKey(offer.contractSpan, energyType);

  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';

  const cashbackTexts = getCashbackTexts(offer?.costDiscounts?.cashback?.type, textualData?.promotionTexts);

  return (
    <SMEFlowProductCard
      id={offer.type}
      title={getTitle(textualDataKey, textualData?.productData?.productDataList as FlowsNewEnergyOfferStep)}
      tag={
        offer.mostPopularChoice
          ? getPromotionText(textualDataKey, textualData?.productData?.productDataList as FlowsNewEnergyOfferStep)
          : undefined
      }
      usps={getUsps(textualDataKey, textualData?.productData?.productDataList as FlowsNewEnergyOfferStep)}
      price={{
        value: offer?.costsMonthly?.[costsType] ?? 0,
        period: checkOfferIncludesDynamicPricingProduct(offer)
          ? textualData?.productData?.perMonthEstimatedLabel?.value
          : textualData?.productData?.perMonthLabel?.value,
      }}
      ribbon={
        cashbackTexts && (
          <Ribbon
            size="small"
            emphasis="high"
            title={format(cashbackTexts.title, {
              [`${offer?.costDiscounts?.cashback?.type}`]: currency.euroNoFractionDigits(
                offer?.costDiscounts?.cashback?.value ?? 0,
              ),
            })}
            text={cashbackTexts.text}
            trigger={cashbackTexts?.trigger}
          />
        )
      }>
      <OfferCardFooter textualData={textualData} flowContext={flowContext} offer={offer} />
    </SMEFlowProductCard>
  );
};
